import {
    Injectable, inject
} from "@angular/core";
import {
    Observable, of, switchMap
} from "rxjs";
import {
    Team, TeamSearchFilterOptions, Permission
} from "@shopliftr/common-js/shared";
import {
    ISearchSort, SearchSortDirection, ArrayUtil, StringUtil
} from "@shopliftr/common-js/data";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { TeamApiClientService } from "../../iapi-application/services/api-client/team/team-api-client.service";
import { COMMON_APP_SERVICE } from "../../application/constants/app-tokens";
import { ITeamAutocompleteOptions } from "./interfaces/team-autocomplete-options";

/**
 * Autocomplete controller for Team entities
 * Provides autocomplete functionality with realm filtering
 */
@Injectable({
    providedIn: "root"
})
export class TeamAutocompleteControllerService implements IAutocompleteController<Team, ITeamAutocompleteOptions> {

    private readonly _teamService = inject(TeamApiClientService);
    private readonly _app = inject(COMMON_APP_SERVICE);

    /**
     * IAutocompleteController function: autocomplete observable for the GenericAutocompleteMatControlComponent
     *
     * @param query - User's search input for server-side filtering
     * @param options - Autocomplete configuration including realms and size limits
     * @returns Observable of filtered team autocomplete results
     */
    autocomplete$(query?: string, options?: ITeamAutocompleteOptions): Observable<IAutocompleteResults<Team>> {
        const sanitizedQuery = StringUtil.sanitizeString(query ?? "");

        const filterOptions = new TeamSearchFilterOptions().setDefaults();
        filterOptions.realms = options?.realms;
        filterOptions.query = sanitizedQuery;

        // ADM2-1391: Temporary solution for limiting access to teams
        // check if the authenticated has TeamAccessRead permission,
        // if so add teamAccess to the includeIds in the filter options
        const hasTeamPermission = this._app.authService.authenticatedUser.hasPermission(Permission.TeamAccessRead);
        if (hasTeamPermission) {
            const teamAccess = this._app.authService.authenticatedUser.teamAccess;
            if (teamAccess && !ArrayUtil.isEmpty(teamAccess)) {
                filterOptions.includeIds = teamAccess;
            }
        }

        // Use custom sort if provided, otherwise default to alphabetical by name (ascending)
        const sort: Array<ISearchSort> = options?.sort ?? [{
            field: "name",
            direction: SearchSortDirection.Ascending
        }];

        return this._teamService.getAll$(filterOptions, sort).pipe(
            switchMap((result: Array<Team>) => {
                const autocompleteOptions = new Array<IAutocompleteOption<Team>>();

                if (result) {
                    result.forEach((item) => {
                        const autocompleteItem: IAutocompleteOption<Team> = {
                            option: this.getAutocompleteDisplayName(item, options),
                            value: item
                        };
                        autocompleteOptions.push(autocompleteItem);
                    });
                }

                const autocompleteResults: IAutocompleteResults<Team> = {
                    options: autocompleteOptions
                };

                return of(autocompleteResults);
            })
        );
    }

    /**
     * Get display name for a team in autocomplete
     */

    getAutocompleteDisplayName(value?: Team, options?: ITeamAutocompleteOptions): string {
        return value?.name || "";
    }

    /**
     * Returns a unique identifier for tracking items in the autocomplete
     */
    trackByIdentifier(index: number, result?: IAutocompleteOption<Team>): any {
        return result?.value?.id;
    }

    /**
     * Returns true if the value does not exist in the items array
     */
    isUnique(value: Team, items: Array<Team>): boolean {
        return !items?.some((item) => item.id === value?.id);
    }
}
